'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { columnConfigApi, type ColumnConfig } from '@parts/api';
import { toast } from '@/lib/toast';
import { useConfirm } from '@/components/common/feedback/ConfirmProvider';

export default function ColumnConfigPage() {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState<ColumnConfig[]>([]);
  const [templates, setTemplates] = useState<ColumnConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<ColumnConfig | null>(null);
  const confirm = useConfirm();

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      // 获取我的配置
      const myConfigsRes = await columnConfigApi.getAll({ includeTemplates: false });
      setConfigs(myConfigsRes.items);

      // 获取团队模板
      const templatesRes = await columnConfigApi.getAll({ 
        onlyTemplates: true, 
        onlyPublic: true 
      });
      setTemplates(templatesRes.items);
    } catch (error) {
      console.error('Failed to load configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await columnConfigApi.setDefault(id);
      toast.success(t.parts.setDefaultSuccess);
      loadConfigs();
    } catch (error) {
      console.error('Failed to set default:', error);
      toast.error('Failed to set default configuration');
    }
  };

  const handleCopy = async (config: ColumnConfig) => {
    const name = prompt(t.parts.configName, `${config.name} (Copy)`);
    if (!name) return;

    try {
      await columnConfigApi.copy(config.id, { name });
      toast.success(t.parts.configCopiedSuccess);
      loadConfigs();
    } catch (error) {
      console.error('Failed to copy config:', error);
      toast.error('Failed to copy configuration');
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: t.parts.configDeleteConfirm,
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await columnConfigApi.delete(id);
      toast.success(t.parts.configDeletedSuccess);
      loadConfigs();
    } catch (error) {
      console.error('Failed to delete config:', error);
      toast.error('Failed to delete configuration');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t.common.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t.parts.columnManagement}
          </h1>
          <p className="text-gray-600">
            {t.parts.columnConfig}
          </p>
        </div>

        {/* Actions */}
        <div className="mb-6 flex gap-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            + {t.parts.createConfig}
          </button>
          <button
            onClick={() => window.location.href = '/parts/custom-fields'}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            {t.parts.customFieldManagement}
          </button>
        </div>

        {/* My Configurations */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {t.parts.myConfigs}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {configs.length === 0 ? (
              <div className="col-span-full text-center py-12 bg-white rounded-lg border border-gray-200">
                <p className="text-gray-500">{t.common.noData}</p>
              </div>
            ) : (
              configs.map((config) => (
                <ConfigCard
                  key={config.id}
                  config={config}
                  onSetDefault={handleSetDefault}
                  onCopy={handleCopy}
                  onDelete={handleDelete}
                  t={t}
                />
              ))
            )}
          </div>
        </div>

        {/* Team Templates */}
        {templates.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {t.parts.teamTemplates}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <ConfigCard
                  key={template.id}
                  config={template}
                  onCopy={handleCopy}
                  t={t}
                  isTemplate
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface ConfigCardProps {
  config: ColumnConfig;
  onSetDefault?: (id: string) => void;
  onCopy: (config: ColumnConfig) => void;
  onDelete?: (id: string) => void;
  t: any;
  isTemplate?: boolean;
}

function ConfigCard({ config, onSetDefault, onCopy, onDelete, t, isTemplate }: ConfigCardProps) {
  const columnCount = config.columns.length;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {config.isDefault && (
              <span className="text-yellow-500">⭐</span>
            )}
            <h3 className="font-semibold text-gray-900">
              {config.name}
            </h3>
          </div>
          {config.description && (
            <p className="text-sm text-gray-600 mb-2">{config.description}</p>
          )}
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
              {columnCount} {t.parts.configColumns}
            </span>
            {config.isTemplate && (
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">
                {t.parts.templateConfig}
              </span>
            )}
            {config.isPublic && (
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                {t.parts.publicConfig}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="text-xs text-gray-500 mb-3">
        {t.common.updated}: {new Date(config.updatedAt).toLocaleDateString()}
      </div>

      <div className="flex gap-2">
        {!isTemplate && onSetDefault && !config.isDefault && (
          <button
            onClick={() => onSetDefault(config.id)}
            className="flex-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            {t.parts.setAsDefault}
          </button>
        )}
        <button
          onClick={() => onCopy(config)}
          className="flex-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
        >
          {t.common.copy}
        </button>
        {!isTemplate && onDelete && (
          <button
            onClick={() => onDelete(config.id)}
            className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
          >
            {t.common.delete}
          </button>
        )}
      </div>
    </div>
  );
}
